import React, { useState, useEffect, useCallback } from 'react';
import {
    FaCar,
    FaSync,
    FaCheck,
    FaTimes,
    FaSpinner,
    FaExclamationTriangle,
    FaCheckCircle,
    FaIdCard,
    FaInfoCircle,
} from 'react-icons/fa';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import {
    reviewLicenceChange,
    approveLicenceChange,
} from '../../services/onboardingService';

const LicenceReview = ({
    serverID,
    purgatoryID,
    employeeID,
    disabled = false,
}) => {
    const [busy, setBusy] = useState(false);
    const [reviewData, setReviewData] = useState(null);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [dvlaAlert, setDvlaAlert] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);

    // Live subscription to employee DVLA status
    useEffect(() => {
        if (!serverID || !employeeID) return;
        const employeeRef = doc(db, 'servers', serverID, 'employees', employeeID);
        const unsub = onSnapshot(employeeRef, (snap) => {
            if (!snap.exists()) return;
            const data = snap.data();
            const dvla = data?.driversLicense?.dvla;
            if (dvla && dvla.validationRequired === true && dvla.validated !== true) {
                setDvlaAlert(true);
            } else {
                setDvlaAlert(false);
            }
        }, (err) => {
            console.error('Failed to listen to employee DVLA state:', err);
        });
        return () => unsub();
    }, [serverID, employeeID]);

    // Reset when target item changes
    useEffect(() => {
        setReviewData(null);
        setError(null);
        setSuccessMessage(null);
    }, [serverID, purgatoryID, employeeID]);

    const handleLoad = useCallback(async () => {
        if (busy || disabled || !serverID || !purgatoryID) return;
        setBusy(true);
        setError(null);
        setSuccessMessage(null);
        try {
            const res = await reviewLicenceChange(serverID, purgatoryID);
            if (employeeID && res?.employeeID && res.employeeID !== employeeID) {
                throw new Error('The matched employee changed. Please refresh the page.');
            }
            setReviewData(res);
        } catch (err) {
            console.error('Failed to review licence change:', err);
            setError(err?.message || 'Failed to review licence change.');
        } finally {
            setBusy(false);
        }
    }, [serverID, purgatoryID, employeeID, busy, disabled]);

    const handleConfirmApprove = async () => {
        if (busy || !reviewData?.proposalID) return;
        setBusy(true);
        setError(null);

        try {
            await approveLicenceChange(serverID, purgatoryID, reviewData.proposalID);
            setSuccessMessage('Licence change saved. DVLA validation has been reset and is now required.');
            setShowApproveModal(false);
            setReviewData(null);
            // Re-fetch updated status
            handleLoad();
        } catch (err) {
            console.error('Failed to approve licence change:', err);
            setError(err?.message || 'Failed to approve driving licence change.');
        } finally {
            setBusy(false);
        }
    };

    const isChanged = reviewData?.outcome === 'NUMBER_CHANGED';

    return (
        <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #edf2f7',
            }}>
                <div>
                    <h3 style={{
                        margin: 0,
                        fontSize: '16px',
                        fontWeight: '800',
                        color: '#183D44',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        <FaCar style={{ color: '#dd6b20' }} />
                        Driving Licence Review
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#718096' }}>
                        Review API licence number updates separately from candidate account status.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={handleLoad}
                    disabled={busy || disabled}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: '#ffffff',
                        color: '#dd6b20',
                        border: '1px solid #fbd38d',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: busy || disabled ? 'not-allowed' : 'pointer',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                >
                    {busy ? (
                        <>
                            <FaSpinner className="fa-spin" />
                            <span>Working…</span>
                        </>
                    ) : (
                        <>
                            <FaSync />
                            <span>{reviewData ? 'Reload Licence Check' : 'Review Latest Licence'}</span>
                        </>
                    )}
                </button>
            </div>

            {/* Live DVLA Alert on Employee Record */}
            {dvlaAlert && (
                <div style={{
                    backgroundColor: '#fffaf0',
                    border: '1px solid #feebc8',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    marginBottom: '14px',
                    color: '#c05621',
                    fontSize: '13px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                }}>
                    <FaExclamationTriangle style={{ marginTop: '2px', flexShrink: 0 }} />
                    <div>
                        Licence number changed — DVLA validation required.
                        <div style={{ fontSize: '12px', fontWeight: '400', color: '#7c2d12', marginTop: '2px' }}>
                            Review the licence details and driving entitlements before validating it in the employee profile.
                        </div>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div style={{
                    backgroundColor: '#fff5f5',
                    border: '1px solid #fed7d7',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    marginBottom: '14px',
                    color: '#c53030',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                }}>
                    <FaExclamationTriangle style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span>{error}</span>
                </div>
            )}

            {/* Success Message */}
            {successMessage && (
                <div style={{
                    backgroundColor: '#f0fff4',
                    border: '1px solid #c6f6d5',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    marginBottom: '14px',
                    color: '#276749',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    <FaCheckCircle style={{ flexShrink: 0 }} />
                    <span>{successMessage}</span>
                </div>
            )}

            {/* Review Results */}
            {reviewData ? (
                <div style={{ marginTop: '12px' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: '12px',
                        marginBottom: '14px',
                    }}>
                        <div style={{
                            backgroundColor: '#f8fafc',
                            border: '1px solid #edf2f7',
                            borderRadius: '8px',
                            padding: '12px',
                        }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', marginBottom: '4px' }}>
                                Current HA Licence
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>
                                {reviewData.currentNumber || 'Not recorded'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                DVLA Validated: <strong>{reviewData.currentValidated ? 'Yes' : 'No'}</strong>
                            </div>
                        </div>

                        <div style={{
                            backgroundColor: isChanged ? '#eff7f4' : '#f8fafc',
                            border: `1px solid ${isChanged ? '#c6f6d5' : '#edf2f7'}`,
                            borderRadius: '8px',
                            padding: '12px',
                        }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: isChanged ? '#2f855a' : '#718096', textTransform: 'uppercase', marginBottom: '4px' }}>
                                API / Onboarding360 Licence
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: isChanged ? '#183D44' : '#2d3748' }}>
                                {reviewData.proposedNumber || 'Not supplied'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                HA Expiry: <strong>{reviewData.currentExpiryDate || 'Not recorded'}</strong> (Unchanged)
                            </div>
                        </div>
                    </div>

                    {isChanged ? (
                        <div style={{
                            backgroundColor: '#fffaf0',
                            border: '1px solid #fbd38d',
                            borderRadius: '8px',
                            padding: '14px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '12px',
                        }}>
                            <div style={{ maxWidth: '600px' }}>
                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#c05621', marginBottom: '4px' }}>
                                    Licence Number Mismatch Detected
                                </div>
                                <div style={{ fontSize: '12.5px', color: '#7c2d12', lineHeight: '1.4' }}>
                                    Approving this change resets DVLA validation. The old check moves to history; a new DVLA check will be required. Expiry date and account access are preserved.
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowApproveModal(true)}
                                disabled={busy || disabled}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#dd6b20',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '9px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: busy || disabled ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                }}
                            >
                                <FaCheck />
                                <span>Approve Licence Change Only</span>
                            </button>
                        </div>
                    ) : (
                        <div style={{
                            padding: '12px 14px',
                            backgroundColor: '#f7fafc',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            fontSize: '13px',
                            color: '#4a5568',
                        }}>
                            {reviewData.outcome === 'NO_SOURCE_NUMBER'
                                ? 'No API licence number was supplied. Existing licence is preserved.'
                                : 'The licence number matches current records. No validation reset is needed.'}
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ fontSize: '12.5px', color: '#718096', fontStyle: 'italic' }}>
                    Click "Review Latest Licence" to compare current HA licence data against Onboarding360 without modifying records.
                </div>
            )}

            {/* Approval Modal */}
            {showApproveModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        maxWidth: '520px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '14px',
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '16px',
                                fontWeight: '700',
                                color: '#183D44',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}>
                                <FaIdCard style={{ color: '#dd6b20' }} />
                                Approve Licence Number Change?
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowApproveModal(false)}
                                disabled={busy}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '16px',
                                    color: '#a0aec0',
                                    cursor: 'pointer',
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div style={{
                            fontSize: '13px',
                            color: '#4a5568',
                            lineHeight: '1.5',
                            marginBottom: '18px',
                        }}>
                            <div style={{
                                backgroundColor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                padding: '10px 14px',
                                borderRadius: '6px',
                                marginBottom: '12px',
                            }}>
                                <div><strong>Current HA: </strong>{reviewData?.currentNumber || 'Not recorded'}</div>
                                <div style={{ color: '#dd6b20', marginTop: '4px' }}>
                                    <strong>New Proposed: </strong>{reviewData?.proposedNumber}
                                </div>
                            </div>

                            <p style={{ margin: '0 0 10px 0', color: '#c05621', fontWeight: '600' }}>
                                DVLA validation will be reset to false. The previous licence and DVLA check will be retained in version history. A new DVLA check is required.
                            </p>
                            <p style={{ margin: 0, color: '#718096', fontSize: '12px' }}>
                                This action does not change expiry date, employee access, or employment status.
                            </p>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px',
                        }}>
                            <button
                                type="button"
                                onClick={() => setShowApproveModal(false)}
                                disabled={busy}
                                style={{
                                    backgroundColor: '#edf2f7',
                                    color: '#4a5568',
                                    border: '1px solid #cbd5e0',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: busy ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmApprove}
                                disabled={busy}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#dd6b20',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: busy ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {busy ? (
                                    <>
                                        <FaSpinner className="fa-spin" />
                                        <span>Approving…</span>
                                    </>
                                ) : (
                                    <span>Approve Licence Change</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LicenceReview;
